import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import SerpContentSnapshot from '@/components/Approval/@components/SerpContentSnapshot';
import { formatSerpText } from '@/utils/approval/serpDocument';
import {
  buildSerpStoreClosureOperationType,
  buildSerpStoreClosurePeriod,
  buildSerpStoreClosureShopName,
  type SerpStoreClosureSource,
} from '@/utils/approval/serpStoreClosure';
import { SerpStoreClosureRoot } from './styles';

type TablesProps = {
  source: SerpStoreClosureSource;
  t: TFunction;
};

const TABLE_CLASS =
  'table table-bordered table-striped text-center table-fixed mb-20';

const SETTLEMENT_ROWS = [
  { label: '공급액', key: 'SPY_AMT' },
  { label: '입금액', key: 'DPST_AMT' },
  { label: '잔액', key: 'SPY_BAL_AMT' },
  { label: '거래보증금', key: 'GRT_AMT' },
  { label: '최종정산 잔액', key: 'STMT_BAL_AMT' },
] as const;

/** 레거시 #APV_SERP_SHOP_CLOSE .form-group 의 제목·표 3개(금액은 응답 글자 그대로) */
const SerpStoreClosureTables = ({ source, t }: TablesProps) => (
  <>
    <h2>{t('매장 철수 정산')}</h2>
    <table className={TABLE_CLASS}>
      <colgroup>
        <col width="20%" />
        <col width="20%" />
        <col width="20%" />
        <col width="40%" />
      </colgroup>
      <tbody>
        <tr>
          <th>{t('브랜드')}</th>
          <th colSpan={3}>{t('매장명')}</th>
        </tr>
        <tr>
          <td>{formatSerpText(source.BRAND_NM)}</td>
          <td colSpan={3}>{buildSerpStoreClosureShopName(source)}</td>
        </tr>
        <tr>
          <th>{t('대표자')}</th>
          <th>{t('사업자등록번호')}</th>
          {/* 레거시 머리글 그대로 — 값은 운영기간 끝 날짜(사용자 결정) */}
          <th>{t('매장명')}</th>
          <th>{t('운영기간')}</th>
        </tr>
        <tr>
          <td>{formatSerpText(source.REP_NM)}</td>
          <td>{formatSerpText(source.BRN)}</td>
          <td>{formatSerpText(source.CUS_DE)}</td>
          <td>{buildSerpStoreClosurePeriod(source)}</td>
        </tr>
      </tbody>
    </table>
    <table className={TABLE_CLASS}>
      <tbody>
        <tr>
          <th colSpan={2} className="alert-primary">
            {t('정산내역')}
          </th>
        </tr>
        <tr>
          <th>{t('구분')}</th>
          <th>{t('금액')}</th>
        </tr>
        {SETTLEMENT_ROWS.map(({ label, key }) => (
          <tr key={key}>
            <th>{t(label)}</th>
            <td>{formatSerpText(source[key])}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <table className={TABLE_CLASS}>
      <tbody>
        <tr>
          <th colSpan={2} className="alert-primary">
            {t('기타 내역')}
          </th>
        </tr>
        <tr>
          <th>{t('운영형태')}</th>
          <td>{buildSerpStoreClosureOperationType(source)}</td>
        </tr>
      </tbody>
    </table>
  </>
);

/** 작성 화면 표 — 저장 content 와 같은 마크업 */
export const SerpStoreClosureTablesView = (props: TablesProps) => (
  <SerpStoreClosureRoot>
    <div className="form-group">
      <SerpStoreClosureTables {...props} />
    </div>
  </SerpStoreClosureRoot>
);

/** attributes.content — 레거시 조회 화면은 이 HTML 만 그대로 출력 */
export const buildSerpStoreClosureContentHtml = (
  source: SerpStoreClosureSource,
  t: TFunction,
): string =>
  renderToStaticMarkup(
    <div className="form-group">
      <SerpStoreClosureTables source={source} t={t} />
    </div>,
  );

/** 저장 content(레거시 문서 포함) 그대로 */
export const SerpStoreClosureSnapshot = ({ content }: { content: string }) => (
  <SerpStoreClosureRoot>
    <SerpContentSnapshot content={content} />
  </SerpStoreClosureRoot>
);
